import { z } from 'zod';
/**
 * Creatures bound to a player: a pet that trots at the side, a mount that carries.
 *
 * Neither is a paper-doll layer. A pet is its own entity in the zone, moved
 * by the server beside its owner; a mount is drawn under the rider,
 * on its own canvas, and changes how fast the server lets the rider walk.
 * Both are items (slots `pet` and `mount`) so the inventory, the ledger and
 * the shop treat them like anything else; the template names the companion.
 */
export const COMPANION_KINDS = ['pet', 'mount'];
export const CompanionDefinitionSchema = z.object({
    id: z.string().min(1),
    kind: z.enum(COMPANION_KINDS),
    /** French, for the shop and the tooltip. */
    name: z.string().min(1),
    /** Logical body sprite key in the manifest, like a class or a mob. */
    bodyKey: z.string().min(1),
    /** Canvas side of the body frames; a mount needs more room than a 32 px tile. */
    canvas: z.number().int().positive(),
    /**
     * Mount: the rider's speed as a multiple of the class's. A rule, not a
     * hint — the server paces the walk by it and rejects anything faster.
     */
    speedMultiplier: z.number().positive().default(1),
    /** Mount: pixels the rider's feet rise above the ground, onto the saddle. */
    seat: z.number().int().min(0).default(0),
    /**
     * Mount: pixels the rider sits back from the middle of the beast, towards
     * its tail, seen from the side. A camel's saddle is on its hump, behind its
     * long neck; a horse's is at its middle.
     */
    seatBack: z.number().int().min(0).default(0),
});
const DEFINITIONS = [
    {
        id: 'fire-hound',
        kind: 'pet',
        name: 'Chien de feu',
        bodyKey: 'body.fire-hound',
        canvas: 32,
    },
    {
        // The seventh day of the account's streak (ADR 0051).
        id: 'light-fox',
        kind: 'pet',
        name: 'Renard de lumière',
        bodyKey: 'body.light-fox',
        canvas: 32,
    },
    {
        // The beta testers' pet (ADR 0083): a bug tamed, from their achievement.
        id: 'glitch',
        kind: 'pet',
        name: 'Glitch',
        bodyKey: 'body.glitch',
        canvas: 32,
    },
    {
        // The Scarecrow pack's crow (ADR 0031), sold in the shop.
        id: 'scarecrow-crow',
        kind: 'pet',
        name: 'Corbeau de minuit',
        bodyKey: 'body.scarecrow-crow',
        canvas: 32,
    },
    {
        // The aurora pack's pet (ADR 0031): a polar fox, sold in the shop.
        id: 'aurora-fox',
        kind: 'pet',
        name: 'Renard polaire de l’Aurore',
        bodyKey: 'body.aurora-fox',
        canvas: 32,
    },
    {
        // The forest chain's last reward (ADR 0047), from the trapper Corvin.
        id: 'forest-fox-cub',
        kind: 'pet',
        name: 'Renardeau de Sylvebois',
        bodyKey: 'body.forest-fox-cub',
        canvas: 32,
    },
    {
        // The swamp chain's last reward (ADR 0047), from the hunter Nella.
        id: 'swamp-caiman',
        kind: 'pet',
        name: 'Petit caïman de Brumevase',
        bodyKey: 'body.swamp-caiman',
        // The caiman's canvas: a long low body whose stride leaves the 32 px square.
        canvas: 36,
    },
    {
        // The shaman pack's pet (ADR 0031): a snowy owl that flies at its
        // master's side instead of trotting, its wings beating in place.
        id: 'snowy-owl',
        kind: 'pet',
        name: 'Harfang des neiges',
        bodyKey: 'body.snowy-owl',
        // Its spread wings are 32 px wide: 36 leaves room for the beat.
        canvas: 36,
    },
    {
        id: 'fire-horse',
        kind: 'mount',
        name: 'Cheval de feu',
        bodyKey: 'body.fire-horse',
        canvas: 48,
        speedMultiplier: 2.2,
        // The saddle sits thirteen pixels above the hooves on the 48 px horse:
        // the rider's belt lands on the back, under the mane (review sheet).
        seat: 13,
    },
    {
        // The meadow chain's last reward (ADR 0047), from the capital's stable:
        // a good deal quicker than a walk, well short of the shop's fire horse.
        id: 'meadow-horse',
        kind: 'mount',
        name: 'Cheval de Havrebourg',
        bodyKey: 'body.meadow-horse',
        canvas: 48,
        speedMultiplier: 1.6,
        seat: 13,
    },
    {
        // The desert's camel drivers' reward (ADR 0047): a little quicker than
        // the capital's horse, still short of the shop's fire horse.
        id: 'desert-camel',
        kind: 'mount',
        name: 'Chameau de Sablerouge',
        bodyKey: 'body.desert-camel',
        canvas: 48,
        speedMultiplier: 1.8,
        // Its back stands a little lower than the horse's: the rider sinks into the hump's saddle.
        seat: 11,
        seatBack: 3,
    },
    {
        // Sold alone in the shop, the quickest mount of the game: three and a
        // half times a walk crosses the world map in about a minute.
        id: 'pegasus',
        kind: 'mount',
        name: 'Pégase',
        bodyKey: 'body.pegasus',
        canvas: 48,
        speedMultiplier: 3.5,
        seat: 13,
    },
    // Sold singly in the shop (`cosmetics/companions.ts`).
    { id: 'black-cat', kind: 'pet', name: 'Chat noir', bodyKey: 'body.black-cat', canvas: 32 },
    { id: 'slime', kind: 'pet', name: 'Slime', bodyKey: 'body.slime', canvas: 32 },
    { id: 'mini-golem', kind: 'pet', name: 'Mini-golem', bodyKey: 'body.mini-golem', canvas: 32 },
    { id: 'mini-yeti', kind: 'pet', name: 'Mini-yéti', bodyKey: 'body.mini-yeti', canvas: 32 },
    {
        // Sold alone in the shop at 9,99 €, between the fire horse (2,2 at 6,99 €)
        // and the pegasus (3,5 at 19,99 €): its price sits about a quarter of the
        // way up, so is its speed, two and a half times a walk.
        id: 'war-wolf',
        kind: 'mount',
        name: 'Loup de guerre',
        bodyKey: 'body.war-wolf',
        canvas: 48,
        speedMultiplier: 2.5,
        // Its back stands as high as the horse's, but its saddle sits behind
        // the armoured shoulders, a little towards the tail (review sheet).
        seat: 13,
        seatBack: 3,
    },
    {
        // The dragonslayer pack's mount (ADR 0031): priced and paced like the fire horse.
        id: 'griffon',
        kind: 'mount',
        name: 'Griffon',
        bodyKey: 'body.griffon',
        canvas: 48,
        speedMultiplier: 2.2,
        // A lion's back stands lower than a horse's: the rider sits between the wings.
        seat: 10,
    },
    {
        // The dragonslayer pack's pet: a hooded hunting falcon standing at the right hand.
        id: 'hunting-falcon',
        kind: 'pet',
        name: 'Faucon de chasse',
        bodyKey: 'body.hunting-falcon',
        canvas: 32,
    },
    {
        // The forest pack's mount (« Esprit sylvestre »): as quick as the founder's fire horse.
        id: 'sylvan-stag',
        kind: 'mount',
        name: 'Cerf sylvestre',
        bodyKey: 'body.sylvan-stag',
        canvas: 48,
        speedMultiplier: 2.2,
        // Its back stands a pixel lower than the fire horse's, between the long legs.
        seat: 12,
    },
    {
        // The swamp pack's pet (« Sorcier des marais »): a frog whose throat glows like a lantern.
        id: 'lantern-frog',
        kind: 'pet',
        name: 'Grenouille-lanterne',
        bodyKey: 'body.lantern-frog',
        canvas: 32,
    },
    {
        // The djinn pack's mount (ADR 0031), a carpet that floats a hand above
        // the ground: as quick as the shop's fire horse. The rider sits cross-legged
        // on it, legs hidden by the riding clips like on a horse, lower than a
        // saddle since the carpet is flat.
        id: 'flying-carpet',
        kind: 'mount',
        name: 'Tapis volant',
        bodyKey: 'body.flying-carpet',
        canvas: 48,
        speedMultiplier: 2.2,
        seat: 8,
    },
    {
        // The raid's reward (ADR 0068 §6), from Ulfar once Shefira has fallen: a
        // drake of her brood, as quick as the shop's fire horse (the user's call,
        // 2026-09-30): the quickest earned in play, behind the sold pegasus.
        id: 'shefira-drake',
        kind: 'mount',
        name: 'Drake de Shefira',
        bodyKey: 'body.shefira-drake',
        canvas: 48,
        speedMultiplier: 2.2,
        seat: 13,
    },
];
export const COMPANIONS = Object.fromEntries(DEFINITIONS.map((raw) => {
    const definition = CompanionDefinitionSchema.parse(raw);
    return [definition.id, definition];
}));
export function getCompanion(id) {
    return COMPANIONS[id];
}
/** « +250 % »: how much quicker than on foot a mount carries, in whole percent. */
export function mountSpeedBonus(mount) {
    return Math.round((mount.speedMultiplier - 1) * 100);
}
/** No mount carries faster: the shop says so, to sell it. */
export function isFastestMount(mount) {
    return Object.values(COMPANIONS).every((other) => other.kind !== 'mount' || other.speedMultiplier <= mount.speedMultiplier);
}
//# sourceMappingURL=companions.js.map